import { useState } from "react";
import { makeStyles, mergeClasses } from "@fluentui/react-components";
import { CheckmarkCircle16Filled } from "@fluentui/react-icons";
import { useTranslation } from "react-i18next";
import ICarouselSlide from "../../components/carousel/ICarouselSlide";
import { dolphinTokens } from "../../utils/Theme";


const useStyles = makeStyles({
    section: {
        maxWidth: "1180px",
        margin: "0 auto",
        padding: "clamp(48px, 6vw, 80px) clamp(20px, 4vw, 40px)",
    },
    tabs: {
        display: "flex",
        flexWrap: "wrap",
        columnGap: "10px",
        rowGap: "10px",
        marginBottom: "28px",
    },
    tab: {
        appearance: "none",
        border: "none",
        cursor: "pointer",
        fontFamily: "inherit",
        padding: "10px 18px",
        borderRadius: "999px",
        fontSize: "14px",
        fontWeight: 600,
        color: dolphinTokens.colorDarkBlue,
        backgroundColor: dolphinTokens.colorLightBlue20,
        transitionProperty: "background-color, color",
        transitionDuration: "150ms",
        ":hover": { backgroundColor: dolphinTokens.colorLightBlue40 },
    },
    tabActive: {
        backgroundColor: dolphinTokens.colorDarkBlue,
        color: "#ffffff",
        ":hover": { backgroundColor: dolphinTokens.colorDarkBlue },
    },
    card: {
        display: "grid",
        gridTemplateColumns: "1fr",
        borderRadius: "20px",
        overflow: "hidden",
        boxShadow: "0 20px 50px rgba(0, 25, 35, 0.14)",
        "@media (min-width: 860px)": {
            gridTemplateColumns: "1.1fr 1fr",
        },
    },
    photo: {
        position: "relative",
        minHeight: "260px",
        backgroundColor: dolphinTokens.colorLightBlue20,
        "@media (min-width: 860px)": {
            minHeight: "420px",
        },
    },
    photoImg: {
        position: "absolute",
        inset: 0,
        width: "100%",
        height: "100%",
        objectFit: "cover",
    },
    content: {
        backgroundColor: dolphinTokens.colorNeutralBackground1,
        padding: "clamp(28px, 4vw, 44px)",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
    },
    pill: {
        alignSelf: "flex-start",
        backgroundColor: dolphinTokens.colorLightBlue20,
        color: dolphinTokens.colorLightBlue,
        fontSize: "12px",
        fontWeight: 700,
        textTransform: "uppercase",
        letterSpacing: "0.02em",
        padding: "6px 14px",
        borderRadius: "999px",
        marginBottom: "16px",
    },
    title: {
        fontSize: "clamp(20px, 2.2vw, 26px)",
        fontWeight: 700,
        color: dolphinTokens.colorDarkBlue,
        lineHeight: 1.25,
        margin: "0 0 12px",
    },
    description: {
        fontSize: "14.5px",
        lineHeight: 1.65,
        color: dolphinTokens.colorDarkBlue80,
        margin: "0 0 18px",
    },
    details: {
        display: "flex",
        flexDirection: "column",
        rowGap: "9px",
        listStyleType: "none",
        padding: 0,
        margin: 0,
    },
    detailItem: {
        display: "flex",
        alignItems: "flex-start",
        columnGap: "10px",
        fontSize: "14px",
        color: dolphinTokens.colorDarkBlue,
    },
    detailIcon: {
        flexShrink: 0,
        color: dolphinTokens.colorLightBlue,
        marginTop: "1px",
    },
});

export default function HomeCarousel() {
    const { t } = useTranslation("home");
    const styles = useStyles();
    const [activeIndex, setActiveIndex] = useState(0);

    const slides: ICarouselSlide[] = [
        {
            header: `${t("carousel.slides.who.pill")}`,
            title: `${t("carousel.slides.who.title")}`,
            description: `${t("carousel.slides.who.description")}`,
            image: {
                url: "/img/home/img-vertical.webp",
                altText: `${t("carousel.slides.who.title")}`
            },
            details: t('carousel.slides.who.details', { returnObjects: true }) as string[]
        },
        {
            header: `${t("carousel.slides.support.pill")}`,
            title: `${t("carousel.slides.support.title")}`,
            description: `${t("carousel.slides.support.description")}`,
            image: {
                url: "/img/home/img-horizontal.webp",
                altText: `${t("carousel.slides.support.title")}`
            },
            details: t('carousel.slides.support.details', { returnObjects: true }) as string[]
        },
        {
            header: `${t("carousel.slides.migrate.pill")}`,
            title: `${t("carousel.slides.migrate.title")}`,
            description: `${t("carousel.slides.migrate.description")}`,
            image: {
                url: "/img/home/img-horizontal2.webp",
                altText: `${t("carousel.slides.migrate.title")}`
            },
            details: t('carousel.slides.migrate.details', { returnObjects: true }) as string[]
        },
        {
            header: `${t("carousel.slides.aiAutomation.pill")}`,
            title: `${t("carousel.slides.aiAutomation.title")}`,
            description: `${t("carousel.slides.aiAutomation.description")}`,
            image: {
                url: "/img/home/ai-automation.webp",
                altText: `${t("carousel.slides.aiAutomation.title")}`
            },
            details: t('carousel.slides.aiAutomation.details', { returnObjects: true }) as string[]
        },
        {
            header: `${t("carousel.slides.aiInsights.pill")}`,
            title: `${t("carousel.slides.aiInsights.title")}`,
            description: `${t("carousel.slides.aiInsights.description")}`,
            image: {
                url: "/img/home/ai-insights.webp",
                altText: `${t("carousel.slides.aiInsights.title")}`
            },
            details: t('carousel.slides.aiInsights.details', { returnObjects: true }) as string[]
        }
    ];

    const active = slides[activeIndex];

    return (
        <section className={styles.section}>
            <div className={styles.tabs} role="tablist">
                {slides.map((slide, index) => (
                    <button
                        key={slide.header}
                        type="button"
                        role="tab"
                        aria-selected={index === activeIndex}
                        className={mergeClasses(styles.tab, index === activeIndex && styles.tabActive)}
                        onClick={() => setActiveIndex(index)}
                    >
                        {slide.header}
                    </button>
                ))}
            </div>

            <div className={styles.card}>
                <div className={styles.photo}>
                    <img className={styles.photoImg} src={active.image.url} alt={active.image.altText} />
                </div>
                <div className={styles.content}>
                    <span className={styles.pill}>{active.header}</span>
                    <h3 className={styles.title}>{active.title}</h3>
                    <p className={styles.description}>{active.description}</p>
                    {Array.isArray(active.details) && active.details.length > 0 && (
                        <ul className={styles.details}>
                            {active.details.map((detail) => (
                                <li key={detail} className={styles.detailItem}>
                                    <CheckmarkCircle16Filled className={styles.detailIcon} />
                                    {detail}
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </div>
        </section>
    );
}